import { Storage } from 'aws-amplify';
import { Skill } from '@amzn/finspace-service-typescript-client';
import { FinSpaceServiceApi } from '../api/finspaceServiceApi';
import { handleApiRequest } from '../api/utils/apiRequest';
import { createStore } from './store-factory';
import { useNotificationStore } from './notification-store';

interface SkillStoreState {
  skills: Skill[];
  isLoading: boolean;
  error: any;
  modalVisible: boolean;
  selectedFile: File[];
  s3Uri: string | null;
  isUploading: boolean;
  uploadProgress: number;
  isModalLoading: boolean;
  fetchSkills: (includeExperimental?: boolean) => Promise<void>;
  openCreateModal: () => Promise<void>;
  closeModal: () => void;
  setSelectedFile: (files: File[]) => void;
  createSkill: () => Promise<void>;
}

const initialModalState = {
  modalVisible: false,
  selectedFile: [] as File[],
  s3Uri: null as string | null,
  isUploading: false,
  uploadProgress: 0,
  isModalLoading: false,
};

export const useSkillStore = createStore<SkillStoreState>('skill', (set, get) => ({
  skills: [],
  isLoading: false,
  error: null,
  ...initialModalState,

  fetchSkills: async (includeExperimental?: boolean) => {
    set({ isLoading: true, error: null });
    try {
      const response = await handleApiRequest(() => FinSpaceServiceApi.getSkills(includeExperimental));
      set({ skills: response.skills || [], isLoading: false });
    } catch (error) {
      set({ error, isLoading: false });
    }
  },

  openCreateModal: async () => {
    set({ isModalLoading: true, error: null });
    try {
      const response = await handleApiRequest(() => FinSpaceServiceApi.getTransferS3URI());
      set({ s3Uri: response.s3URI, modalVisible: true, isModalLoading: false });
    } catch (error) {
      set({ error, isModalLoading: false });
      useNotificationStore.getState().error('Failed to initialize upload. Please try again.');
    }
  },

  closeModal: () => set({ ...initialModalState }),

  setSelectedFile: (files: File[]) => set({ selectedFile: files }),

  createSkill: async () => {
    const { s3Uri, selectedFile } = get();
    if (!s3Uri || selectedFile.length === 0) return;

    set({ isUploading: true, error: null, uploadProgress: 0 });
    try {
      const key = s3Uri.split('://')[1].split('/').slice(1).join('/');

      await Storage.put(key, selectedFile[0], {
        progressCallback: (progress) => {
          set({ uploadProgress: (progress.loaded / progress.total) * 100 });
        },
        customPrefix: { public: '' },
      });

      await handleApiRequest(() => FinSpaceServiceApi.createSkill(s3Uri));

      set({ ...initialModalState });
      useNotificationStore.getState().success('Skill created successfully!');
      get().fetchSkills(true);
    } catch (error) {
      set({ error: 'Upload failed. Please try again.', isUploading: false });
    }
  },
}));
